import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Languages, Play, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ENTITY_LABELS = {
  product: "Товары",
  news: "Новости",
  brand: "Бренды",
  all: "Всё",
};

export default function AdminTranslations() {
  const [entity, setEntity] = useState("all");
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [concurrency, setConcurrency] = useState(5);
  const [job, setJob] = useState(null); // {id, total, processed, failed, status}
  const [stats, setStats] = useState(null); // {products: {total, missing}, news: {...}, brands: {...}}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  // Load entity counts so the admin sees how much work is queued.
  const loadStats = async () => {
    try {
      // Reuse public listings — these already exclude hidden.
      // For products we use the admin include_hidden to count everything.
      const [p, n, b] = await Promise.all([
        axios.get(`${API}/products?include_hidden=true&limit=2000`, { withCredentials: true }),
        axios.get(`${API}/news?include_hidden=true&limit=500`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/brands?limit=200`, { withCredentials: true }),
      ]);
      const missing = (arr, fields) => arr.filter(d =>
        fields.some(f => (d[f] || "").trim() && (!(d[`${f}_kz`] || "").trim() || !(d[`${f}_en`] || "").trim()))
      ).length;
      setStats({
        product: { total: p.data.length, missing: missing(p.data, ["name", "description"]) },
        news:    { total: n.data.length, missing: missing(n.data, ["title", "content"]) },
        brand:   { total: b.data.length, missing: missing(b.data, ["name", "description"]) },
      });
    } catch (e) {
      // ignore — stats are decorative
    }
  };

  useEffect(() => { loadStats(); }, []);

  // Poll active job every 2s.
  useEffect(() => {
    if (!job?.id || job.status === "done" || job.status === "done_with_errors") return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/admin/i18n/jobs/${job.id}`, { withCredentials: true });
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "done_with_errors") {
          clearInterval(pollRef.current);
          loadStats();
        }
      } catch (e) {
        clearInterval(pollRef.current);
      }
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [job?.id, job?.status]);

  const start = async () => {
    setError("");
    setBusy(true);
    setJob(null);
    try {
      const r = await axios.post(`${API}/admin/i18n/retranslate-all`, {
        entity, only_missing: onlyMissing, concurrency,
      }, { withCredentials: true });
      setJob({ id: r.data.job_id, total: r.data.queued, processed: 0, failed: 0, status: "running" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Не удалось запустить перевод");
    } finally {
      setBusy(false);
    }
  };

  const pct = job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;
  const totalToProcess = stats && entity !== "all"
    ? (onlyMissing ? stats[entity]?.missing : stats[entity]?.total)
    : (stats ? (onlyMissing
        ? (stats.product?.missing || 0) + (stats.news?.missing || 0) + (stats.brand?.missing || 0)
        : (stats.product?.total || 0) + (stats.news?.total || 0) + (stats.brand?.total || 0))
      : null);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <Languages size={26} color="#C5A059" />
          Переводы контента
        </h2>
        <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 4 }}>
          Автоперевод названий и описаний на казахский и английский через Gemini AI
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 24 }}>
          {["product", "news", "brand"].map(k => {
            const s = stats[k];
            const done = s.total - s.missing;
            return (
              <div key={k} data-testid={`i18n-stats-${k}`} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "14px 16px" }}>
                <div style={{ fontFamily: "Manrope", fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>{ENTITY_LABELS[k]}</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 6 }}>
                  <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#111", fontWeight: 600 }}>{done} / {s.total}</div>
                  <div style={{ fontFamily: "Manrope", fontSize: 11, color: s.missing > 0 ? "#dc2626" : "#16a34a" }}>
                    {s.missing > 0 ? `${s.missing} без перевода` : "✓ все переведены"}
                  </div>
                </div>
                <div style={{ height: 4, background: "#f3f4f6", borderRadius: 2, marginTop: 8 }}>
                  <div style={{ height: "100%", width: s.total > 0 ? `${(done / s.total) * 100}%` : "0%", background: "#16a34a", borderRadius: 2, transition: "width 0.3s" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Controls */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontFamily: "Manrope", fontSize: 15, fontWeight: 700, color: "#111", margin: "0 0 16px 0" }}>Запустить batch-перевод</h3>

        <div style={{ display: "grid", gap: 14 }}>
          <label style={lbl}>
            Что переводить
            <select data-testid="i18n-entity-select" value={entity} onChange={e => setEntity(e.target.value)} style={sel} disabled={!!job && job.status !== "done"}>
              <option value="all">Всё (товары + новости + бренды)</option>
              <option value="product">Только товары</option>
              <option value="news">Только новости</option>
              <option value="brand">Только бренды</option>
            </select>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Manrope", fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input data-testid="i18n-only-missing" type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} style={{ width: 16, height: 16 }} />
            <div>
              <strong>Только без перевода</strong>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                Отключите, чтобы пере-перевести ВСЕ записи (полный force-rerun, медленнее)
              </div>
            </div>
          </label>

          <label style={lbl}>
            Параллельность (1-10)
            <input data-testid="i18n-concurrency" type="number" min={1} max={10} value={concurrency} onChange={e => setConcurrency(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))} style={inp} disabled={!!job && job.status !== "done"} />
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              Сколько товаров переводим одновременно. 5 — оптимально. Больше = быстрее, но риск 429-rate-limit от Gemini.
            </div>
          </label>

          {totalToProcess !== null && (
            <div style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 6, padding: "10px 12px", fontFamily: "Manrope", fontSize: 13, color: "#78350f" }}>
              К обработке: <strong>{totalToProcess}</strong> {totalToProcess === 1 ? "запись" : "записей"}.
              Ожидаемое время: ~<strong>{Math.ceil(totalToProcess * 15 / concurrency / 60)}</strong> мин (≈15 сек на запись, Gemini 2.5 Pro).
              <div style={{ fontSize: 11, color: "#92400e", marginTop: 4 }}>
                Перевод идёт на сервере — можно закрыть эту страницу, прогресс сохранится.
              </div>
            </div>
          )}

          {error && (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "10px 12px", borderRadius: 6, fontFamily: "Manrope", fontSize: 13 }}>{error}</div>
          )}

          <button
            data-testid="i18n-start-btn"
            onClick={start}
            disabled={busy || (job && job.status === "running")}
            style={{
              background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A",
              border: "none", borderRadius: 6, padding: "12px 24px", cursor: busy ? "not-allowed" : "pointer",
              fontFamily: "Manrope", fontWeight: 700, fontSize: 14,
              display: "inline-flex", alignItems: "center", gap: 8, justifyContent: "center",
              opacity: (busy || (job && job.status === "running")) ? 0.6 : 1,
            }}
          >
            {busy ? <><Loader2 size={16} className="animate-spin" /> Запускаем…</>
                  : (job && job.status === "running") ? <><Loader2 size={16} className="animate-spin" /> Идёт перевод…</>
                  : <><Play size={16} /> Запустить</>}
          </button>
        </div>
      </div>

      {/* Job progress */}
      {job && (
        <div data-testid="i18n-job-progress" style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <h3 style={{ fontFamily: "Manrope", fontSize: 15, fontWeight: 700, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 8 }}>
              {job.status === "done"
                ? <><CheckCircle2 size={18} color="#16a34a" /> Готово</>
                : job.status === "done_with_errors"
                ? <><AlertCircle size={18} color="#f59e0b" /> Завершено с ошибками</>
                : <><Loader2 size={18} className="animate-spin" color="#C5A059" /> Идёт перевод…</>}
            </h3>
            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#94a3b8" }}>job_id: {job.id?.slice(0, 8)}…</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "Manrope", fontSize: 13, color: "#374151", marginBottom: 6 }}>
              <span>Обработано: <strong>{job.processed}</strong> из <strong>{job.total}</strong> ({pct}%)</span>
              {job.failed > 0 && <span style={{ color: "#dc2626" }}><AlertCircle size={12} style={{ display: "inline" }} /> {job.failed} ошибок</span>}
            </div>
            <div style={{ height: 8, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: job.status === "done" ? "#16a34a" : "#C5A059", borderRadius: 4, transition: "width 0.5s" }} />
            </div>
          </div>
          {job.status === "done" && (
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#16a34a", marginTop: 10 }}>
              ✓ Перевод завершён. Откройте каталог/новости — текст автоматически отобразится в выбранном языке.
            </div>
          )}
          {job.status === "done_with_errors" && (
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#f59e0b", marginTop: 10, lineHeight: 1.5 }}>
              ⚠ Часть записей не удалось перевести ({job.failed} из {job.total}). Самая частая причина — закончился бюджет Emergent LLM Key. 
              <br />Пополните в <strong>Profile → Universal Key → Add Balance</strong> (или включите auto-top-up) и запустите снова — этот раз обработаются только незаведённые.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const lbl = { display: "block", fontFamily: "Manrope", fontSize: 12, fontWeight: 600, color: "#374151" };
const sel = { width: "100%", marginTop: 6, border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontFamily: "Manrope", fontSize: 13, outline: "none", background: "white", cursor: "pointer", boxSizing: "border-box" };
const inp = { width: 80, marginTop: 6, border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontFamily: "Manrope", fontSize: 13, outline: "none" };
